/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Container, Form, Tab, Tabs, Modal, Card } from 'react-bootstrap';
import {
  accountDetailsRequestAction,
  resendVerificationEmailRequestAction,
  enable2faRequestAction,
  generateQrRequestAction,
  create2faRequestAction,
  logoutRequestAction
} from '../../store/account/userActions';
import { checkUserVerified } from '../../helpers/verificationHelper';
import './Profile.scss';
import { useHistory } from 'react-router-dom';
import { setAlertAction } from '../../store/alert/alertActions';

// Import modern icons
import { 
  User, 
  Mail, 
  Shield, 
  CheckCircle, 
  Key, 
  Smartphone, 
  QrCode, 
  Copy, 
  Settings,
  Lock,
  Unlock,
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

function Profile({ ...props }) {
  const {
    user,
    accountDetailsRequest,
    resendVerificationEmailRequest,
    enable2faRequest,
    generateQrRequest,
    create2faRequest,
    create2faData,
    enable2faData,
    enable2faError,
    generateQrData,
    logoutRequest,
    setAlert,
    twoFactorAuth,
  } = props;
  
  const [activeTab, setActiveTab] = useState(props.location.pathname.substring(1));
  const [switchValue, setSwitchValue] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [inputValue, setInputValue] = useState('');
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    accountDetailsRequest();
  }, []);

  useEffect(() => {
    if (
      props.location.hash.substring(1) === 'verify' ||
      props.location.hash.substring(1) === 'kyc' ||
      props.location.hash.substring(1) === '2fa' ||
      props.location.hash.substring(1) === 'profile' ||
      props.location.hash.substring(1) === 'connections'
    ) {
      setActiveTab(props.location.hash.substring(1));
    }
  }, [props.location]);

  useEffect(() => {
    if (enable2faData && isFinished) {
      setShowModal(false);
      setTimeout(() => {
        logoutRequest();
      }, 1000);
      setInputValue('');
    }
    setSwitchValue(user.two_factor_auth)
  }, [props.user, props.enable2faData, props.generateQrData, props.create2faData]);

  function verifyEmail() {
    resendVerificationEmailRequest();
  }

  const handleSelect = (e) => {
    setActiveTab(e);
    props.history.replace({
      hash: `${e}`,
    });
  };

  const handleSwitchChange = async () => {
    try {
      if (switchValue) {
        setShowModal(true);
        setStep(2);
      } else {
        const payload = {};
        await create2faRequest(payload);
        await generateQrRequest(payload);
        setShowModal(true);
        setStep(1);
      }
    } catch (error) {
      console.error('Error handling switch change:', error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setStep(1);
    setShowSecret(false);
  };

  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handleBackButton = () => {
    if (switchValue && step === 2) {
      handleCloseModal();
    } else {
      setStep((prevStep) => prevStep - 1);
    }
  };

  const handleCopyToClipboard = (input) => {
    const text = typeof input === 'object' && input !== null
      ? Object.values(input).join('')
      : String(input);
  
    navigator.clipboard.writeText(text)
      .then(() => {
        setAlert({
          title: 'Success!',
          text: 'Text copied to clipboard',
          variant: 'success',
          outTimeMS: 2000,
        });
      })
      .catch((err) => {
        console.error('Failed to copy text:', err);
      });
  };
  

  const handleInputChange = (e) => {
    let value = e.target.value;
    value = value.replace(/-/g, "");
    value = value.replace(/[^0-9]/g, "").slice(0, 6);
    if (value.length > 3) {
      value = value.slice(0, 3) + "-" + value.slice(3);
    }
    setInputValue(value);
  };

  const handleFinish = async () => {
    try {
      const totp_code = inputValue.replace(/-/g, "");
      const payload = {
        totp_code: totp_code,
        history
      };
      await enable2faRequest(payload);
      setIsFinished(true)
    } catch (error) {
      console.error('Error finishing 2FA setup:', error);
    }
  };

  // Modern Status Badge Component
  const StatusBadge = ({ verified, children }) => (
    <div className={`modern-status-badge ${verified ? 'verified' : 'unverified'}`}>
      {verified ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
      <span>{children}</span>
    </div>
  );

  // Modern Input Group Component
  const ModernInputGroup = ({ label, value, icon: Icon, disabled = true, action = null }) => (
    <div className="modern-input-group">
      <label className="input-label">
        <Icon size={18} />
        {label}
      </label>
      <div className="input-wrapper">
        <Form.Control
          value={value}
          type="text"
          disabled={disabled}
          className="modern-input"
        />
        {action && (
          <div className="input-action">
            {action}
          </div>
        )}
      </div>
    </div>
  );

  // Modern Tab Item Component
  const ModernTabTitle = ({ icon: Icon, title }) => (
    <div className="modern-tab-title">
      <Icon size={18} />
      <span>{title}</span>
    </div>
  );
  
  return (
    <div className="modern-profile">
      <Container className="profile-container">
        {/* Header Section */}
        <div className="profile-header">
          <div className="header-content">
            <h1 className="profile-title">
              <Settings size={32} className="title-icon" />
              Account Settings
            </h1>
            <p className="profile-subtitle">
              Manage your account information and security settings
            </p>
          </div>
        </div>

        {/* Modern Tabs */}
        <div className="modern-tabs-wrapper">
          <Tabs
            defaultActiveKey="profile"
            activeKey={activeTab ? activeTab : 'profile'}
            className="modern-tabs"
            onSelect={(e) => handleSelect(e)}
          >
            <Tab
              eventKey="profile"
              title={<ModernTabTitle icon={User} title="Profile" />}
            >
              {activeTab === 'profile' && (
                <div className="tab-content-wrapper">
                  <Card className="modern-profile-card">
                    <div className="profile-card-header">
                      <div className="header-info">
                        <h3>Personal Information</h3>
                      </div>
                    </div>

                    <div className="profile-form">
                      <ModernInputGroup
                        label="Full Name"
                        value={user.full_name || ''}
                        icon={User}
                      />

                      <ModernInputGroup
                        label="Email Address"
                        value={user.email || ''}
                        icon={Mail}
                        action={
                          checkUserVerified(user) ? (
                            <div className="verification-badge verified">
                              <CheckCircle size={16} />
                              <span>Verified</span>
                            </div>
                          ) : (
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={verifyEmail}
                              className="verify-btn"
                            >
                              <Mail size={14} />
                              Verify Email
                            </Button>
                          )
                        }
                      />
                    </div>
                  </Card>
                </div>
              )}
            </Tab>

            <Tab
              eventKey="2fa"
              title={<ModernTabTitle icon={Shield} title="Two-Factor Authentication" />}
            >
              {activeTab === '2fa' && (
                <div className="tab-content-wrapper">
                  <Card className="modern-2fa-card">
                    <div className="twofa-card-header">
                      <div className="header-info">
                        <h3>Two-Factor Authentication</h3>
                        <p>Add an extra layer of security to your account</p>
                      </div>
                      <div className={`security-status ${switchValue ? 'enabled' : 'disabled'}`}>
                        {switchValue ? <Lock size={20} /> : <Unlock size={20} />}
                        <span>{switchValue ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>

                    <div className="twofa-content">
                      <div className="twofa-info">
                        <div className="info-icon">
                          <Smartphone size={24} />
                        </div>
                        <div className="info-text">
                          <h4>Authenticator App Required</h4>
                          <p>Use Google Authenticator, Authy, or similar apps to generate verification codes</p>
                        </div>
                      </div>

                      <div className="twofa-toggle">
                        <Form.Check
                          type="switch"
                          id="2fa-switch"
                          checked={switchValue}
                          onChange={handleSwitchChange}
                          className="modern-switch"
                        />
                        <div className="toggle-label">
                          <span className="toggle-title">
                            {switchValue ? 'Disable' : 'Enable'} 2FA
                          </span>
                          <span className="toggle-description">
                            {switchValue 
                              ? 'Turn off two-factor authentication' 
                              : 'Secure your account with 2FA'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
      </Container>

      {/* Modern 2FA Setup Modal */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal}
        className="modern-2fa-modal"
        centered
        size="md"
      >
        <div className="modal-header-modern">
          <div className="modal-header-content">
            <div className="modal-icon">
              {step === 1 ? <QrCode size={24} /> : <Key size={24} />}
            </div>
            <div className="modal-title-content">
              <h4>{step === 1 ? 'Setup Authenticator' : 'Verify Code'}</h4>
              <p className='text-white'>{step === 1 ? 'Scan QR code or enter secret key' : 'Enter the 6-digit code from your app'}</p>
            </div>
          </div>
          <button 
            className="modal-close-btn" 
            onClick={handleCloseModal}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="modal-body-modern">
          {step === 1 && create2faData && generateQrData && !switchValue && (
            <div className="qr-setup-content">
              {/* Secret Key Section */}
              <div className="secret-key-section">
                <div className="secret-key-header">
                  <span className="secret-label">Secret Key</span>
                  <button
                    type="button"
                    className="show-hide-btn"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                    {showSecret ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="secret-key-wrapper">
                  <div className="secret-key-value">
                    {showSecret ? Object.values(create2faData) : '••••••••••••••••••••••••••••••••'}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleCopyToClipboard(Object.values(create2faData).join(''))}
                    className="copy-btn"
                  >
                    <Copy size={14} />
                    Copy
                  </Button>
                </div>
              </div>

              {/* QR Code Section */}
              <div className="qr-code-section">
                <div className="qr-code-wrapper">
                  <img
                    src={`data:image/png;base64,${Object.values(generateQrData).join('')}`}
                    alt="QR Code"
                    className="qr-code-image"
                  />
                </div>
                <div className="qr-instructions">
                  <h5>Scan with your authenticator app</h5>
                  <p>Open your authenticator app and scan this QR code, or manually enter the secret key above.</p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="verification-content">
              <div className="verification-form">
                <Form.Group className="code-input-group">
                  <Form.Label className="code-label">
                    <Key size={16} />
                    Enter 6-digit verification code
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="000-000"
                    value={inputValue}
                    onChange={handleInputChange}
                    className="verification-input"
                    autoFocus
                  />
                </Form.Group>
                <div className="verification-help">
                  <p>Enter the 6-digit code from your authenticator app</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer-modern">
          {step !== 1 && (
            <Button 
              variant="outline-secondary" 
              onClick={handleBackButton}
              className="modal-back-btn"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => (step !== 2 ? handleNextStep() : handleFinish())}
            className="modal-next-btn"
            disabled={step === 2 && inputValue.length < 7}
          >
            {step !== 2 ? (
              <>
                Next
                <ArrowRight size={16} />
              </>
            ) : (
              <>
                <Check size={16} />
                Finish
              </>
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    accountVerifiedData: state.userReducer.accountVerifiedData,
    user: state.userReducer.user,
    enable2faData: state.userReducer.enable2faData,
    enable2faError: state.userReducer.enable2faError,
    generateQrData: state.userReducer.generateQrData,
    generateQrError: state.userReducer.generateQrError,
    create2faData: state.userReducer.create2faData,
    create2faError: state.userReducer.create2faError,
    getKyc: state.userReducer.getKyc,
    kyc: state.userReducer.kyc,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
    resendVerificationEmailRequest: (payload) => {
      dispatch(resendVerificationEmailRequestAction(payload));
    },
    enable2faRequest: (payload) => {
      dispatch(enable2faRequestAction(payload));
    },
    generateQrRequest: (payload) => {
      dispatch(generateQrRequestAction(payload));
    },
    create2faRequest: (payload) => {
      dispatch(create2faRequestAction(payload));
    },
    logoutRequest: (payload) => {
      dispatch(logoutRequestAction(payload));
    },
    setAlert: (payload) => {
      dispatch(setAlertAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Profile);